<script lang="ts">
  import { Text } from '@squareup/public-web-design-system';
  import type { PwNextAudienceMomentProps } from './types';

  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import ArrowNorthEast from '../PwNextPartials/PwNextIcons/ArrowNorthEast.svelte';

  const component = 'PwNextAudienceMoment';

  // #region Props
  type $$Props = PwNextAudienceMomentProps;
  export let entryId: PwNextAudienceMomentProps['entryId'];
  export let heading: PwNextAudienceMomentProps['heading'];
  export let subcategoryCards: PwNextAudienceMomentProps['subcategoryCards'];
  export let textMarquee: PwNextAudienceMomentProps['textMarquee'] = undefined;
  export let headingOptions: PwNextAudienceMomentProps['headingOptions'];
  // #endregion

  // #region Setup
  let elSection: HTMLElement;
  let wWrapper: number;
  let wCards: number;
  let translateX: number = 0;

  $: wDiff = Math.max(wCards - wWrapper, 0);
  // #endregion

  // #region Handlers
  const handleScroll = () => {
    const top = Math.min(elSection?.getBoundingClientRect().top, 0);
    translateX = (Math.max(top, -wDiff) / wCards) * 100;
  };

  const handleFocus = (numcard: number) => {
    const scrollAmount = (numcard / (subcategoryCards.length - 1)) * wDiff;
    window.scrollTo({
      top: elSection.offsetTop + scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleResize = () => {
    if (wDiff === 0) {
      translateX = 0;
    }
  };
  // #endregion
</script>

<svelte:window on:scroll={handleScroll} on:resize={handleResize} />
<section
  class="{component} {component}-{entryId}-box-properties"
  data-entry-id={entryId}
  bind:this={elSection}
  style:--width-adjustment={`${wDiff}px`}
>
  <div class="{component}-wrapper" bind:clientWidth={wWrapper}>
    <div class="{component}-heading">
      <Text variant={headingOptions.variant} as={headingOptions.as} isBalanced={headingOptions.isBalanced}>
        {heading}
      </Text>
    </div>
    <div class="{component}-cards-wrapper">
      <div class="{component}-cards" bind:clientWidth={wCards} style:transform="translateX({translateX}%)">
        {#each subcategoryCards as card, i}
          <a on:focus={() => handleFocus(i)} class="{component}-card" href={card.url}>
            <div class="{component}-card-image">
              <DynamicChildrenComponent children={[card.image]} />
            </div>
            <div class="{component}-card-title">
              <Text variant="200">
                {card.title}
                <span class="card-arrow">
                  <ArrowNorthEast />
                </span>
              </Text>
            </div>
          </a>
        {/each}
      </div>
    </div>
    {#if textMarquee}
      <div class="{component}-marquee">
        <DynamicChildrenComponent children={[textMarquee]} />
      </div>
    {/if}
  </div>
</section>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;
  @use '@squareup/public-web-design-system/dist/styles/mixins/_text-style' as textStyle;

  .PwNextAudienceMoment {
    --container-border-radius: 1rem;

    display: flex;
    justify-content: center;
    align-items: center;

    @include media.at('small') {
      align-items: start;
      height: calc(100vh + var(--width-adjustment));
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: sticky;
      top: 0;
      width: var(--container-width-current);

      @include media.at('small') {
        height: 100vh;
        padding: var(--space-100) 0;
      }
    }

    &-heading {
      margin-bottom: var(--space-60);
      text-align: center;

      @include media.at('medium') {
        margin-bottom: var(--space-80);
      }
    }

    &-cards-wrapper {
      position: relative;

      @include media.at('small') {
        height: 40.8rem;
      }
      @include media.at('large') {
        height: calc(var(--container-width-current) * 0.2757);
      }
    }

    &-cards {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: var(--space-20);
      position: relative;
      min-width: 100%;
      height: 100%;

      @include media.at('small') {
        flex-direction: row;
        gap: var(--space-10);
        position: absolute;
      }
    }

    &-card {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      position: relative;
      overflow: hidden;

      @include media.at('small') {
        width: 28rem;
        padding: var(--space-20);
        --color: var(--color-white);
        --color-text: var(--color-white);
        border-radius: var(--container-border-radius);
      }
      @include media.at('large') {
        width: calc((var(--container-width-current) - var(--space-10) * 4) / 5);
      }

      &-title {
        --color-text: var(--color-black);

        @include media.at('small') {
          --color-text: var(--color-white);
        }

        z-index: 2;

        :global {
          .Text {
            display: flex;
            justify-content: space-between;

            @include media.at('small') {
              justify-content: start;
              margin-bottom: -2px; // line up text baseline with padding
            }
          }
        }

        .card-arrow {
          display: flex;
          align-items: flex-end;
          flex: 0 0 auto;
          margin-left: 0.5ex;
          margin-bottom: 0.5ex;
          :global {
            svg {
              width: 1.1ex;
              height: 1ex;
            }
          }
        }
      }

      &-image {
        display: none;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-gray-05);
        transition: transform var(--duration-default) var(--easing-standard);

        @include media.at('small') {
          display: block;
        }

        :global {
          .AproposImageComponent {
            overflow: hidden;
            height: 100%;
            .ResponsiveImagePartial {
              height: 100%;
              img {
                height: 100% !important;
                object-fit: cover;
              }
            }
          }
        }

        &::before {
          content: '';
          z-index: 1;
          display: block;
          position: absolute;
          width: 100%;
          height: 100%;
          // eased gradient starting 33% from the bottom, going down
          background: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.75) 0%,
            rgba(0, 0, 0, 0.5535) 6.27%,
            rgba(0, 0, 0, 0.40575) 11.22%,
            rgba(0, 0, 0, 0.2865) 15.51%,
            rgba(0, 0, 0, 0.2085) 18.645%,
            rgba(0, 0, 0, 0.1455) 21.45%,
            rgba(0, 0, 0, 0.0945) 24.09%,
            rgba(0, 0, 0, 0.05625) 26.466%,
            rgba(0, 0, 0, 0.0315) 28.413%,
            rgba(0, 0, 0, 0.01575) 30.03%,
            rgba(0, 0, 0, 0.006) 31.416%,
            rgba(0, 0, 0, 0.0015) 32.406%,
            rgba(0, 0, 0, 0) 33%
          );
        }
      }

      &:focus,
      &:hover {
        .PwNextAudienceMoment-card-image {
          transform: scale(var(--zoom-in-scale));
        }
      }
    }

    &-marquee {
      width: 100vw;
      margin-top: var(--space-60);
      margin-left: calc((var(--container-width-current) - 100vw) / 2);
    }
  }
</style>
